import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'react-hot-toast';
import {
  FaDownload,
  FaEye,
  FaCheckCircle,
  FaSpinner,
  FaCopy,
  FaCheck,
  FaShoppingCart,
  FaCreditCard
} from 'react-icons/fa';
import PaymentModal from '../components/PaymentModal';

// API base URL'ini environment variable'dan al
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const api = axios.create({
  baseURL: API_BASE_URL
});

const Result = () => {
  const { id } = useParams();
  const location = useLocation();
  
  console.log('Result component rendered with id:', id);
  const [policyData, setPolicyData] = useState(null);
  
  // Check for payment success/failure in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const source = urlParams.get('source');
    
    if (source === 'generator') {
      // This is a payment callback from generator
      if (location.pathname.includes('payment-success')) {
        toast.success('Payment successful! Your document is being generated.');
      } else if (location.pathname.includes('payment-failed')) {
        toast.error('Payment failed. Please try again.');
      }
    }
  }, [location.search, location.pathname]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [generatingDocument, setGeneratingDocument] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [showBulkPurchase, setShowBulkPurchase] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedDocumentForPayment, setSelectedDocumentForPayment] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentType, setPaymentType] = useState('single');
  const [copiedLinks, setCopiedLinks] = useState({});

  const documentTypes = React.useMemo(() => [
    {
      id: 'privacy-policy',
      name: 'Privacy Policy',
      description: 'Privacy policy based on your project and actual data practices',
      price: 5
    },
    {
      id: 'terms-of-service',
      name: 'Terms of Service',
      description: 'Legal terms and conditions for your service usage',
      price: 5
    },
    {
      id: 'cookie-policy',
      name: 'Cookie Policy',
      description: 'Detailed cookie usage and tracking policy',
      price: 5
    },
    {
      id: 'data-processing-agreement',
      name: 'Data Processing Agreement',
      description: 'GDPR compliant data processing agreement',
      price: 5
    },
    {
      id: 'disclaimer',
      name: 'Disclaimer',
      description: 'Legal disclaimer and liability limitations',
      price: 5
    },
    {
      id: 'refund-policy',
      name: 'Refund Policy',
      description: 'Clear refund and cancellation terms',
      price: 5
    }
  ], []);

  const fetchPolicyData = useCallback(async () => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      const response = await api.get(`/api/privacy-policy/${id}`);
      console.log('Policy data response:', response.data);
      if (response.data.success) {
        setPolicyData(response.data.data);
      } else {
        setError('Failed to fetch policy data');
      }
    } catch (err) {
      console.error('Error fetching policy data:', err);
      setError(err.response?.data?.message || 'Failed to fetch policy data');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchPolicyData();
    }
  }, [id, fetchPolicyData]);

  useEffect(() => {
    if (location.state?.policyData) {
      setPolicyData(location.state.policyData);
      setIsLoading(false);
    }
  }, [location.state]);

  useEffect(() => {
    if (policyData?.publicationId) {
      // Mevcut additional dökümanları çek
      fetchAdditionalDocuments(policyData.publicationId);
    } else {
      // Eğer publicationId yoksa boş liste set et
      setAdditionalDocuments([]);
    }
  }, [policyData?.publicationId]);

  const fetchAdditionalDocuments = async (publicationId) => {
    if (!publicationId) return;
    
    try {
      const response = await api.get(`/api/privacy-policy/related/${publicationId}`);
      if (response.data.success && response.data.documents) {
        const documents = response.data.documents.map(doc => ({
          documentType: doc.documentType,
          publicationId: doc.publicationId,
          publicationUrl: `${API_BASE_URL}/api/publication/${doc.publicationId}`,
          htmlUrl: `${API_BASE_URL}/api/privacy-policy/view/${doc.publicationId}`,
          documentTitle: doc.documentTitle,
        }));
        setAdditionalDocuments(documents);
      } else {
        setAdditionalDocuments([]);
      }
    } catch (error) {
      console.error('Error fetching additional documents:', error);
      setAdditionalDocuments([]);
    }
  };

  const handleDownload = async () => {
    console.log('handleDownload called with policyData:', policyData);
    if (!policyData?.publicationId) {
      console.log('No publicationId found, returning');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Fetching PDF from:', `/api/privacy-policy/download/${policyData.publicationId}`);
      const response = await fetch(`${API_BASE_URL}/api/privacy-policy/download/${policyData.publicationId}`);
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (response.ok) {
        const blob = await response.blob();
        console.log('Blob size:', blob.size);
        console.log('Blob type:', blob.type);
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Document type'a göre dosya adını belirle
        const getDocumentFileName = (documentType, appName, publicationId) => {
          const documentNames = {
            'privacy-policy': 'Privacy_Policy',
            'terms-of-service': 'Terms_of_Service',
            'cookie-policy': 'Cookie_Policy',
            'data-processing-agreement': 'Data_Processing_Agreement',
            'disclaimer': 'Disclaimer',
            'refund-policy': 'Refund_Policy'
          };
          
          const documentName = documentNames[documentType] || 'Document';
          const cleanAppName = appName.replace(/[^a-zA-Z0-9]/g, '_');
          return `${cleanAppName}_${documentName}.pdf`;
        };
        
        const fileName = getDocumentFileName(policyData.documentType, policyData.appName, policyData.publicationId);
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        console.log('Download completed successfully');
      } else {
        const errorText = await response.text();
        console.error('Download failed with status:', response.status);
        console.error('Error response:', errorText);
        alert('Download failed. Please try again.');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Download failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = () => {
    console.log('PolicyData:', policyData);
    if (!policyData?.publicationId) {
      console.log('No publicationId found');
      return;
    }
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    const url = `${backendUrl}/api/publication/${policyData.publicationId}`;
    console.log('Opening URL:', url);
    window.open(url, '_blank');
  };

  const handleCopy = async () => {
    console.log('PolicyData for copy:', policyData);
    if (!policyData?.publicationId) {
      console.log('No publicationId found for copy');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const link = `${backendUrl}/api/publication/${policyData.publicationId}`;
      console.log('Copying link:', link);
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Copy error:', error);
    }
  };

  const generateAdditionalDocument = async (documentType) => {
    if (!policyData) return;
    
    // Döküman ücretli mi kontrol et
    const documentInfo = documentTypes.find(doc => doc.id === documentType);
    if (documentInfo && documentInfo.price > 0) {
      // Ücretli döküman için ödeme dialog'unu göster
      setSelectedDocumentForPayment(documentInfo);
      setShowPaymentModal(true);
      return;
    }
    
    // Ücretsiz döküman için direkt generate et
    await generateDocument(documentType);
  };

  const generateDocument = async (documentType, paymentStatus = 'test') => {
    if (!policyData) return;
    setGeneratingDocument(documentType);
    try {
      // Generator verilerini localStorage'dan al
      const savedGeneratorData = localStorage.getItem('generatorData');
      let generatorData = null;
      
      if (savedGeneratorData) {
        try {
          generatorData = JSON.parse(savedGeneratorData);
        } catch (e) {
          console.error('Error parsing generator data:', e);
        }
      }
      
      // Eğer generator verisi varsa onu kullan, yoksa policyData'dan al
      const companyInfo = generatorData ? {
        companyName: generatorData.companyName,
        companyEmail: generatorData.companyEmail,
        appName: generatorData.appName,
        serviceDescription: generatorData.serviceDescription,
        legalBasis: generatorData.legalBasis,
        childrenPrivacyDetails: generatorData.childrenPrivacyDetails,
        projectId: generatorData.projectId,
        productId: generatorData.productId,
        platforms: generatorData.platforms,
        websiteUrl: generatorData.websiteUrl,
        companyAddress: generatorData.companyAddress,
        contactPerson: generatorData.contactPerson,
        companyType: generatorData.companyType,
        platform: generatorData.platforms,
        appType: generatorData.appType,
        dataTypes: generatorData.dataTypes,
        premiumFeatures: generatorData.premiumFeatures,
        selectedThirdPartyServices: generatorData.selectedThirdPartyServices,
        extraDataFields: generatorData.extraDataFields,
        iosAppUrl: generatorData.iosAppUrl,
        androidAppUrl: generatorData.androidAppUrl,
        useThirdParty: generatorData.useThirdParty,
        // Privacy policy detaylı verilerini ekle
        dataUsage: generatorData.dataUsage,
        userRights: generatorData.userRights,
        targetAudience: generatorData.targetAudience,
        thirdPartyServices: generatorData.thirdPartyServices,
        legalCompliance: generatorData.legalCompliance,
        securityMeasures: generatorData.securityMeasures,
        cookiePolicy: generatorData.cookiePolicy,
        dataRetention: generatorData.dataRetention,
        industry: generatorData.industry,
        customClauses: generatorData.customClauses
      } : {
        companyName: policyData.companyName,
        companyEmail: policyData.companyEmail,
        appName: policyData.appName,
        serviceDescription: policyData.serviceDescription,
        legalBasis: policyData.legalBasis,
        childrenPrivacyDetails: policyData.childrenPrivacyDetails,
        projectId: policyData.projectId,
        productId: policyData.productId,
        platforms: policyData.platforms,
        websiteUrl: policyData.websiteUrl,
        companyAddress: policyData.companyAddress,
        contactPerson: policyData.contactPerson,
        companyType: policyData.companyType,
        platform: policyData.platforms || policyData.platform,
        appType: policyData.appType,
        // PolicyData'dan da privacy policy verilerini al
        dataTypes: policyData.dataTypes,
        premiumFeatures: policyData.premiumFeatures,
        selectedThirdPartyServices: policyData.selectedThirdPartyServices,
        extraDataFields: policyData.extraDataFields,
        dataUsage: policyData.dataUsage,
        userRights: policyData.userRights,
        targetAudience: policyData.targetAudience,
        thirdPartyServices: policyData.thirdPartyServices,
        legalCompliance: policyData.legalCompliance,
        securityMeasures: policyData.securityMeasures,
        cookiePolicy: policyData.cookiePolicy,
        dataRetention: policyData.dataRetention,
        industry: policyData.industry,
        customClauses: policyData.customClauses
      };
      
      const response = await fetch(`${API_BASE_URL}/api/privacy-policy/generate-additional`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentType,
          loginEmail: policyData.loginEmail || policyData.companyEmail,
          companyInfo,
          paymentStatus: paymentStatus
        }),
      });
      if (response.ok) {
        const result = await response.json();
        
        // Yeni döküman generate edildiğinde listeyi yeniden çek
        await fetchAdditionalDocuments(policyData.publicationId);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
        const documentUrl = `${backendUrl}/api/publication/${result.publicationId || result.data?.publicationId}`;
        toast.success(`${documentType.replace('-', ' ').toUpperCase()} generated successfully!`, {
          duration: 5000,
          action: {
            label: 'View',
            onClick: () => window.open(documentUrl, '_blank')
          }
        });
      } else {
        const errorData = await response.json();
        console.error('Backend error:', errorData);
        
        // Daha kullanıcı dostu hata mesajları
        let errorMessage = 'Error generating document. Please check your data and try again.';
        
        if (errorData.error) {
          if (errorData.error.includes('Missing required fields')) {
            errorMessage = `Missing required information: ${errorData.missingFields?.join(', ')}. Please go back to the generator and fill in all required fields.`;
          } else if (errorData.error.includes('Company name')) {
            errorMessage = 'Company name is required. Please go back to the generator and fill in your company information.';
          } else if (errorData.error.includes('Website URL')) {
            errorMessage = 'Website URL is required. Please go back to the generator and fill in your website information.';
          } else if (errorData.error.includes('Platform information')) {
            errorMessage = 'Platform information is required. Please go back to the generator and select at least one platform.';
          } else if (errorData.error.includes('App type')) {
            errorMessage = 'App type is required. Please go back to the generator and select an app type.';
          } else {
            errorMessage = errorData.error;
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        toast.error(errorMessage, {
          duration: 8000,
          action: {
            label: 'Go to Generator',
            onClick: () => window.location.href = '/generator'
          }
        });
      }
    } catch (error) {
      console.error('Document generation error:', error);
      toast.error('Network error or server issue. Please try again later.');
    } finally {
      setGeneratingDocument(null);
    }
  };

  const handleBulkPayment = async (paymentMethod) => {
    if (paymentMethod === 'paytr') {
      // Show PayTR modal for bulk payment
      setShowPaymentDialog(true);
      setShowPaymentDialog(false);
      return;
    }
    
    setIsProcessingPayment(true);
    try {
      // Burada gerçek ödeme işlemi yapılacak
      console.log(`Processing ${paymentMethod} payment for bulk purchase`);
      
      // Şimdilik test modunda devam et
      await generateBulkDocuments('test');
    } catch (error) {
      console.error('Bulk payment error:', error);
      toast.error('Error processing bulk payment.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleBulkTestPayment = async () => {
    setIsProcessingPayment(true);
    try {
      await generateBulkDocuments('test');
    } catch (error) {
      console.error('Bulk test payment error:', error);
      toast.error('Error processing bulk test payment.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const toggleDocumentSelection = (documentId) => {
    setSelectedDocuments(prev => 
      prev.includes(documentId) 
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const getTotalPrice = () => {
    return selectedDocuments.length * 5; // Her döküman $5
  };

  const handleBulkPurchase = async () => {
    if (selectedDocuments.length === 0) {
      alert('Please select at least one document.');
      return;
    }

    const totalCost = getTotalPrice();
    
    // Eğer ücretli dökümanlar varsa ödeme dialogu göster
    if (totalCost > 0) {
      setShowPaymentDialog(true);
      setPaymentAmount(totalCost);
      setPaymentType('bulk');
      return;
    }

    // Ücretsiz dökümanlar için direkt generate et
    await generateBulkDocuments('test');
  };

  const generateBulkDocuments = async (paymentStatus = 'test') => {
    setIsProcessingPayment(true);
    try {
      // Generator verilerini localStorage'dan al
      const savedGeneratorData = localStorage.getItem('generatorData');
      let generatorData = null;
      
      if (savedGeneratorData) {
        try {
          generatorData = JSON.parse(savedGeneratorData);
        } catch (e) {
          console.error('Error parsing generator data:', e);
        }
      }
      
      // Eğer generator verisi varsa onu kullan, yoksa policyData'dan al
      const companyInfo = generatorData ? {
        companyName: generatorData.companyName,
        companyEmail: generatorData.companyEmail,
        appName: generatorData.appName,
        websiteUrl: generatorData.websiteUrl,
        companyType: generatorData.companyType,
        platform: generatorData.platforms,
        appType: generatorData.appType,
        dataTypes: generatorData.dataTypes,
        premiumFeatures: generatorData.premiumFeatures,
        selectedThirdPartyServices: generatorData.selectedThirdPartyServices,
        extraDataFields: generatorData.extraDataFields,
        iosAppUrl: generatorData.iosAppUrl,
        androidAppUrl: generatorData.androidAppUrl,
        useThirdParty: generatorData.useThirdParty
      } : {
        companyName: policyData.companyName,
        companyEmail: policyData.companyEmail,
        appName: policyData.appName,
        websiteUrl: policyData.websiteUrl,
        companyType: policyData.companyType,
        platform: policyData.platform,
        appType: policyData.appType,
      };
      
      const response = await fetch(`${API_BASE_URL}/api/privacy-policy/generate-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentTypes: selectedDocuments,
          loginEmail: policyData.loginEmail || policyData.companyEmail,
          companyInfo,
          totalCost: getTotalPrice(),
          paymentStatus: paymentStatus
        }),
      });
      if (response.ok) {
        const result = await response.json();
        if (result.data && result.data.documents) {
          // Toplu generate işlemi tamamlandığında listeyi yeniden çek
          await fetchAdditionalDocuments(policyData.publicationId);
        }
        if (result.data && result.data.documents && result.data.documents.length > 0) {
          toast.success(`${selectedDocuments.length} documents generated successfully!`, {
            duration: 8000,
            action: {
              label: 'View Documents',
              onClick: () => {
                const firstDoc = result.data.documents[0];
                window.open(firstDoc.publicationUrl, '_blank');
                if (result.data.documents.length > 1) {
                  const otherDocs = result.data.documents.slice(1);
                  const docLinks = otherDocs.map(doc => doc.publicationUrl).join('\n');
                  alert(`Other document links:\n${docLinks}`);
                }
              }
            }
          });
        } else {
          toast.success(`${selectedDocuments.length} documents generated successfully!`);
        }
        setSelectedDocuments([]);
        setShowBulkPurchase(false);
        setShowPaymentDialog(false);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Error generating documents.');
      }
    } catch (error) {
      console.error('Bulk generation error:', error);
      toast.error('Error generating documents.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const getDocumentTitle = (doc) => {
    if (doc.documentTitle) return doc.documentTitle;
    if (doc.documentType) {
      const map = {
        'terms-of-service': 'Terms of Service',
        'cookie-policy': 'Cookie Policy',
        'data-processing-agreement': 'Data Processing Agreement',
        'disclaimer': 'Disclaimer',
        'refund-policy': 'Refund Policy',
        'acceptable-use-policy': 'Acceptable Use Policy'
      };
      return map[doc.documentType] || doc.documentType.replace(/-/g, ' ');
    }
    return 'Unknown';
  };

  const getDocumentFileName = (doc) => {
    const title = getDocumentTitle(doc);
    const cleanTitle = title.toLowerCase().replace(/\s+/g, '-');
    const cleanAppName = (doc.appName || 'Document').replace(/[^a-zA-Z0-9]/g, '_');
    return `${cleanAppName}_${cleanTitle}.pdf`;
  };

  const getDocumentTypeName = (documentType) => {
    const documentNames = {
      'privacy-policy': 'Privacy Policy',
      'terms-of-service': 'Terms of Service',
      'cookie-policy': 'Cookie Policy',
      'data-processing-agreement': 'Data Processing Agreement',
      'disclaimer': 'Disclaimer',
      'refund-policy': 'Refund Policy',
      'acceptable-use-policy': 'Acceptable Use Policy'
    };
    return documentNames[documentType] || 'Document';
  };

  // PayTR payment success handler
  const handlePayTRSuccess = async (paymentResult) => {
    setIsProcessingPayment(true);
    
    try {
      if (selectedDocumentForPayment) {
        // Single document payment
        await generateDocument(selectedDocumentForPayment.id, 'completed');
        setShowPaymentDialog(false);
        setSelectedDocumentForPayment(null);
        toast.success(`Payment successful! ${selectedDocumentForPayment.name} generated.`);
      } else if (selectedDocuments.length > 0) {
        // Bulk payment
        await generateBulkDocuments('completed');
        setShowPaymentDialog(false);
        setSelectedDocuments([]);
        setShowBulkPurchase(false);
        toast.success(`Payment successful! ${selectedDocuments.length} documents generated.`);
      }
    } catch (error) {
      console.error('Error processing PayTR payment:', error);
      toast.error('Payment processing failed. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // New payment handlers for PaymentModal
  const handlePayWithCreditCard = async () => {
    // Show PayTR modal for real payment
    setShowPaymentDialog(true);
    setShowPaymentModal(false);
  };

  const handleSkipPayment = async () => {
    // Test mode - skip payment
    if (selectedDocumentForPayment) {
      await generateDocument(selectedDocumentForPayment.id, 'test');
      setShowPaymentModal(false);
      setSelectedDocumentForPayment(null);
      toast.success(`Test mode: ${selectedDocumentForPayment.name} generated successfully!`);
    }
  };

  const handleCancelPayment = () => {
    setShowPaymentModal(false);
    setSelectedDocumentForPayment(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading policy data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/generator" className="btn btn-primary">
            Generate New Policy
          </Link>
        </div>
      </div>
    );
  }

  if (!policyData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No Policy Data Found</h2>
          <Link to="/generator" className="btn btn-primary">
            Generate New Policy
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <Toaster position="top-right" />
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-8">
          <FaCheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {policyData?.documentType === 'terms-of-service' ? 'Terms of Service Generated!' :
             policyData?.documentType === 'cookie-policy' ? 'Cookie Policy Generated!' :
             policyData?.documentType === 'data-processing-agreement' ? 'Data Processing Agreement Generated!' :
             policyData?.documentType === 'disclaimer' ? 'Disclaimer Generated!' :
             policyData?.documentType === 'refund-policy' ? 'Refund Policy Generated!' :
             'Privacy Policy Generated!'}
          </h1>
          <p className="text-gray-600">Your document has been successfully created and is ready for use.</p>
        </div>

        {/* Policy Information */}
        <div className="card mb-8">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Policy Information</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Company Name</label>
                <p className="text-gray-900">{policyData?.companyName}</p>
              </div>
              <div>
                <label className="form-label">App/Service Name</label>
                <p className="text-gray-900">{policyData?.appName}</p>
              </div>
              <div>
                <label className="form-label">Company Type</label>
                <p className="text-gray-900 capitalize">{policyData?.companyType}</p>
              </div>
              <div>
                <label className="form-label">App Type</label>
                <p className="text-gray-900 capitalize">{policyData?.appType}</p>
              </div>
              <div>
                <label className="form-label">Platforms</label>
                <div className="flex flex-wrap gap-1 mt-1 justify-center">
                  {policyData?.platforms?.map((platform, index) => (
                    <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                      {platform === 'website' ? 'Web' : 
                       platform === 'ios' ? 'iOS' : 
                       platform === 'android' ? 'Android' : platform}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <label className="form-label">Website URL</label>
                <p className="text-gray-900">{policyData?.websiteUrl || 'Not specified'}</p>
              </div>
              <div>
                <label className="form-label">Generated Date</label>
                <p className="text-gray-900">
                  {new Date(policyData?.generatedAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <label className="form-label">Policy ID</label>
                <p className="text-gray-900 font-mono text-sm">{policyData?.publicationId}</p>
              </div>
              <div>
                <label className="form-label">Payment Status</label>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  policyData?.paymentStatus === 'completed' || policyData?.paymentStatus === 'test' 
                    ? 'bg-green-100 text-green-800' 
                    : policyData?.totalCost === 0 || policyData?.paymentStatus === 'free'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {policyData?.paymentStatus === 'completed' ? 'Completed' :
                   policyData?.paymentStatus === 'test' ? 'Test Mode' :
                   policyData?.totalCost === 0 || policyData?.paymentStatus === 'free' ? 'Free' :
                   policyData?.paymentStatus === 'pending' ? 'Pending' : policyData?.paymentStatus}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Download PDF */}
          <div className="card hover-lift flex flex-col justify-between min-h-[220px]">
            <div className="card-body text-center flex flex-col flex-1 justify-between">
              <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <FaDownload className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Download PDF</h3>
              <p className="text-sm text-gray-600 mb-4 min-h-[40px] flex items-center justify-center">
                Get a professional PDF version of your {getDocumentTypeName(policyData?.documentType).toLowerCase()}
              </p>
              <button
                onClick={handleDownload}
                disabled={isLoading}
                className="btn btn-primary w-full mt-auto"
              >
                {isLoading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Downloading...
                  </>
                ) : (
                  'Download PDF'
                )}
              </button>
            </div>
          </div>

          {/* View Online */}
          <div className="card hover-lift flex flex-col justify-between min-h-[220px]">
            <div className="card-body text-center flex flex-col flex-1 justify-between">
              <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <FaEye className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">View Online</h3>
              <p className="text-sm text-gray-600 mb-4 min-h-[40px] flex items-center justify-center">
                View your {getDocumentTypeName(policyData?.documentType).toLowerCase()} in a web browser
              </p>
              <button
                onClick={handleView}
                className="btn btn-primary w-full mt-auto"
              >
                View Online
              </button>
            </div>
          </div>

          {/* Copy Link */}
          <div className="card hover-lift flex flex-col justify-between min-h-[220px]">
            <div className="card-body text-center flex flex-col flex-1 justify-between">
              <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <FaCopy className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Share Link</h3>
              <p className="text-sm text-gray-600 mb-4 min-h-[40px] flex items-center justify-center">
                Get a shareable link for your {getDocumentTypeName(policyData?.documentType).toLowerCase()}
              </p>
              <button
                onClick={handleCopy}
                className="btn btn-primary w-full mt-auto"
              >
                {copied ? (
                  <>
                    <FaCheck className="mr-2" />
                    Copied!
                  </>
                ) : (
                  'Copy Link'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Additional Legal Documents */}
        <div className="card mb-8">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div className="text-left">
                <h3 className="text-lg font-semibold">Additional Legal Documents</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Generate additional legal documents for your business using the same information
                </p>
              </div>
              <button
                onClick={() => setShowBulkPurchase(!showBulkPurchase)}
                className="btn btn-primary flex items-center gap-2"
              >
                <FaShoppingCart />
                {showBulkPurchase ? 'Cancel' : 'Bulk Purchase'}
              </button>
            </div>
          </div>
          <div className="card-body">
            {showBulkPurchase && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">Selected Documents</h4>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Total: ${getTotalPrice()}</p>
                    <p className="text-xs text-gray-500">{selectedDocuments.length} document(s) selected</p>
                  </div>
                </div>
                <button
                  onClick={handleBulkPurchase}
                  disabled={selectedDocuments.length === 0 || isProcessingPayment}
                  className="btn btn-primary w-full flex items-center justify-center gap-2"
                >
                  {isProcessingPayment ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaCreditCard />
                      Purchase All Selected (${getTotalPrice()})
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* Generate edilmemiş dökümanlar */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Available Documents to Generate</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(() => {
                  const currentDocumentType = policyData?.documentType || 'privacy-policy';
                  const availableDocuments = documentTypes.filter(doc => doc.id !== currentDocumentType);
                  const generatedDocumentTypes = additionalDocuments.map(doc => doc.documentType);
                  const ungeneratedDocuments = availableDocuments.filter(doc => !generatedDocumentTypes.includes(doc.id));
                  
                  return ungeneratedDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      className={`border rounded-lg p-4 transition-colors flex flex-col justify-between min-h-[210px] cursor-pointer ${
                        selectedDocuments.includes(doc.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                      onClick={showBulkPurchase ? (e) => {
                        if (
                          e.target.tagName.toLowerCase() === 'button' ||
                          e.target.tagName.toLowerCase() === 'input'
                        ) return;
                        toggleDocumentSelection(doc.id);
                      } : undefined}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {showBulkPurchase && (
                            <input
                              type="checkbox"
                              checked={selectedDocuments.includes(doc.id)}
                              onChange={() => toggleDocumentSelection(doc.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          )}
                          <h4 className="font-semibold text-gray-900">{doc.name}</h4>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          doc.price > 0 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {doc.price > 0 ? `$${doc.price}` : 'Free'}
                        </span>
                      </div>
                      <div className="flex-1 flex flex-col justify-between">
                        <p className="text-sm text-gray-600 mb-3 min-h-[40px] flex items-center">
                          {doc.description}
                        </p>
                        {!showBulkPurchase && (
                          <button
                            onClick={() => generateAdditionalDocument(doc.id)}
                            disabled={generatingDocument === doc.id}
                            className="btn btn-primary w-full text-sm mt-auto"
                          >
                            {generatingDocument === doc.id ? (
                              <>
                                <FaSpinner className="animate-spin mr-2" />
                                Generating...
                              </>
                            ) : (
                              `Generate ${doc.name}`
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
            {/* Generate edilmiş dökümanlar */}
            {additionalDocuments.length > 0 && (
              <div className="mt-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Generated Documents</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b">Document</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-b">ID</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-b">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {additionalDocuments.map(doc => (
                        <tr key={doc.publicationId || doc.documentType} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-700 font-medium capitalize text-left">
                            {getDocumentTitle(doc) || 'N/A'}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-500 font-mono text-left">{doc.publicationId || 'N/A'}</td>
                          <td className="px-4 py-2 flex flex-wrap gap-2 justify-end items-center">
                            <button 
                              className="btn btn-outline btn-xs" 
                              onClick={async () => {
                                try {
                                  console.log('Downloading additional document:', doc);
                                  const response = await fetch(`${API_BASE_URL}/api/privacy-policy/download/${doc.publicationId}`);
                                  if (response.ok) {
                                    const blob = await response.blob();
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = getDocumentFileName(doc);
                                    document.body.appendChild(a);
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                    document.body.removeChild(a);
                                    console.log('Additional document download completed');
                                  } else {
                                    const errorText = await response.text();
                                    console.error('Additional document download failed:', response.status, errorText);
                                    alert('Download failed. Please try again.');
                                  }
                                } catch (error) {
                                  console.error('Additional document download error:', error);
                                  alert('Download failed. Please try again.');
                                }
                              }}
                            >
                              Download PDF
                            </button>
                            <a href={doc.publicationUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-xs">View Online</a>
                            <button
                              className="btn btn-outline btn-xs flex items-center"
                              onClick={async () => {
                                await navigator.clipboard.writeText(doc.publicationUrl);
                                setCopiedLinks(prev => ({ ...prev, [doc.publicationId]: true }));
                                setTimeout(() => setCopiedLinks(prev => ({ ...prev, [doc.publicationId]: false })), 2000);
                              }}
                            >
                              {copiedLinks[doc.publicationId] ? (
                                <>
                                  <FaCheck className="mr-1" /> Copied!
                                </>
                              ) : (
                                'Copy Link'
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="text-center">
          <Link
            to="/generator"
            className="btn btn-outline mr-4"
          >
            Generate Another Policy
          </Link>
          <Link
            to="/"
            className="btn btn-primary"
          >
            Back to Home
          </Link>
        </div>
      </div>

      {/* Payment Modal */}
      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal && selectedDocumentForPayment}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedDocumentForPayment(null);
        }}
        onPayWithCreditCard={handlePayWithCreditCard}
        onSkipPayment={handleSkipPayment}
        onCancel={handleCancelPayment}
        isLoading={isProcessingPayment}
        productInfo={{
          name: selectedDocumentForPayment?.name || 'Document',
          description: selectedDocumentForPayment?.description || 'Document generation',
          price: selectedDocumentForPayment?.price || 0
        }}
      />

      {/* Bulk Payment Modal */}
      <PaymentModal
        isOpen={showPaymentDialog && paymentType === 'bulk'}
        onClose={() => {
          setShowPaymentDialog(false);
          setPaymentAmount(0);
          setPaymentType('single');
        }}
        onPayWithCreditCard={() => handleBulkPayment('paytr')}
        onSkipPayment={handleBulkTestPayment}
        onCancel={() => {
          setShowPaymentDialog(false);
          setPaymentAmount(0);
          setPaymentType('single');
        }}
        isLoading={isProcessingPayment}
        productInfo={{
          name: `${selectedDocuments.length} Documents`,
          description: `Bulk purchase of ${selectedDocuments.length} documents`,
          price: paymentAmount
        }}
        items={selectedDocuments.map(docId => {
          const doc = documentTypes.find(d => d.id === docId);
          return {
            title: doc?.name || docId,
            price: doc?.price || 5
          };
        })}
      />

    </div>
  );
};

export default Result;
